import React, { useEffect, useState } from "react";
import { Modal, Form, Button, Dropdown, Tag, Switch, message, InputNumber } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, PoweroffOutlined, MoreOutlined } from "@ant-design/icons";

import {
  getVendorPlansApi,
  createVendorPlanApi,
  updateVendorPlanApi,
  deleteVendorPlanApi,
} from "../../services/api";
import AppTable from "../../common/AppTable";
import AppInput from "../../common/FormInputs/AppInput";

const ACCENT = "#534ab7";

export default function VendorPlans() {
  const [plans,   setPlans]   = useState([]);
  const [open,     setOpen]     = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [form] = Form.useForm();

  const fetchPlans = async () => {
    try {
      const res = await getVendorPlansApi();
      setPlans((res.data.data || []).slice().sort((a, b) => a.sortOrder - b.sortOrder));
    } catch {
      message.error("Failed to load vendor plans");
    }
  };

  useEffect(() => { fetchPlans(); }, []);

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ active: true, sortOrder: 0, monthlyFee: 0, maxActiveProducts: 50, commissionRatePercent: 9 });
    setOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      name:                  record.name,
      monthlyFee:            record.monthlyFee,
      maxActiveProducts:     record.maxActiveProducts,
      commissionRatePercent: record.commissionRatePercent,
      sortOrder:             record.sortOrder,
      active:                record.active,
    });
    setOpen(true);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setLoading(true);
    try {
      if (editing) {
        await updateVendorPlanApi(editing.id, values);
        message.success("Plan updated");
      } else {
        await createVendorPlanApi(values);
        message.success("Plan added");
      }
      setOpen(false);
      fetchPlans();
    } catch (err) {
      message.error(err?.response?.data?.data || "Failed to save plan");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (record) => {
    try {
      await updateVendorPlanApi(record.id, { ...record, active: !record.active });
      message.success(record.active ? "Plan deactivated" : "Plan activated");
      fetchPlans();
    } catch {
      message.error("Failed to update status");
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete this plan?",
      content: "Vendors currently on this plan will keep their existing limits until reassigned.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteVendorPlanApi(id);
          message.success("Plan deleted");
          fetchPlans();
        } catch (err) {
          message.error(err?.response?.data?.data || "Failed to delete plan");
        }
      },
    });
  };

  const columns = [
    {
      title: "Plan",
      dataIndex: "name",
      render: (v) => <div style={{ fontWeight: 600, color: "#111827" }}>{v}</div>,
    },
    {
      title: "Monthly Fee",
      dataIndex: "monthlyFee",
      align: "center",
      render: (v) => <span style={{ fontSize: 12, color: "#6B7280" }}>{v ? `₹${v}` : "Free"}</span>,
    },
    {
      title: "Product Limit",
      dataIndex: "maxActiveProducts",
      align: "center",
    },
    {
      title: "Commission",
      dataIndex: "commissionRatePercent",
      align: "center",
      render: (v) => <span>{v}%</span>,
    },
    {
      title: "Sort",
      dataIndex: "sortOrder",
      width: 70,
      align: "center",
    },
    {
      title: "Status",
      dataIndex: "active",
      width: 90,
      align: "center",
      render: (v) => <Tag color={v ? "success" : "default"}>{v ? "Active" : "Off"}</Tag>,
    },
    {
      title: "Actions",
      width: 70,
      align: "center",
      render: (_, r) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "edit", label: "Edit", icon: <EditOutlined />, onClick: () => openEdit(r) },
              {
                key: "toggle",
                label: r.active ? "Deactivate" : "Activate",
                icon: <PoweroffOutlined />,
                onClick: () => handleToggleActive(r),
              },
              { type: "divider" },
              { key: "delete", label: "Delete", icon: <DeleteOutlined />, danger: true, onClick: () => handleDelete(r.id) },
            ],
          }}
        >
          <Button icon={<MoreOutlined />} size="small" />
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Vendor Plans</div>
          <div className="page-sub">{plans.length} plans — product limits &amp; commission rates for vendor tiers</div>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          style={{ background: ACCENT, border: "none", borderRadius: 8 }}
          onClick={openAdd}
        >
          Add Plan
        </Button>
      </div>

      <AppTable columns={columns} data={plans} />

      <Modal
        title={editing ? `Edit — ${editing.name}` : "Add Plan"}
        open={open}
        width={520}
        okText={editing ? "Save Changes" : "Add Plan"}
        okButtonProps={{ style: { background: ACCENT, border: "none" }, loading }}
        onOk={handleSubmit}
        onCancel={() => { setOpen(false); setEditing(null); form.resetFields(); }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <AppInput
            name="name"
            label="Plan Name"
            rules={[{ required: true, message: "Plan name is required" }]}
            placeholder="e.g. Growth"
          />

          <div style={{ display: "flex", gap: 12 }}>
            <Form.Item name="monthlyFee" label="Monthly Fee (₹)" style={{ flex: 1 }} rules={[{ required: true }]}>
              <InputNumber min={0} style={{ width: "100%", borderRadius: 8 }} />
            </Form.Item>
            <Form.Item name="maxActiveProducts" label="Product Limit" style={{ flex: 1 }} rules={[{ required: true }]}>
              <InputNumber min={1} style={{ width: "100%", borderRadius: 8 }} />
            </Form.Item>
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <Form.Item name="commissionRatePercent" label="Commission (%)" style={{ flex: 1 }} rules={[{ required: true }]}>
              <InputNumber min={0} max={100} step={0.5} style={{ width: "100%", borderRadius: 8 }} />
            </Form.Item>
            <Form.Item name="sortOrder" label="Sort Order" style={{ flex: 1 }}>
              <InputNumber min={0} max={999} style={{ width: "100%", borderRadius: 8 }} />
            </Form.Item>
          </div>

          <Form.Item name="active" label="Active" valuePropName="checked">
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
